<template>
  <div class="tier-manager">
    <!-- Header -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <div class="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 class="text-lg font-semibold text-gray-900">{{ $t('loyalty.title.tiers') }}</h3>
        <button
          @click="showCreateDialog = true"
          class="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 shadow-sm hover:shadow-md">
          <i class="mdi mdi-plus mr-2"></i>
          {{ $t('loyalty.button.add_tier') }}
        </button>
      </div>

      <!-- Tiers List -->
      <div class="p-6" v-loading="loading">
        <div v-if="tiers.length === 0" class="text-center py-12">
          <i class="mdi mdi-information-outline text-4xl text-gray-400 mb-2"></i>
          <p class="text-gray-500">{{ $t('message.no_data') }}</p>
        </div>
        <div v-else class="space-y-4">
          <div
            v-for="(tier, index) in sortedTiers"
            :key="tier.id"
            class="bg-gradient-to-r from-white to-gray-50 border-2 rounded-xl p-6 hover:shadow-lg transition-all duration-200"
            :class="tier.is_active ? 'border-blue-300' : 'border-gray-200'">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <div class="flex items-center space-x-4 mb-4">
                  <div class="flex-shrink-0">
                    <div class="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
                      {{ index + 1 }}
                    </div>
                  </div>
                  <div>
                    <h4 class="text-xl font-bold text-gray-900">{{ tier.name }}</h4>
                    <p class="text-sm text-gray-500 font-mono">{{ tier.code }}</p>
                  </div>
                  <span
                    v-if="tier.is_active"
                    class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {{ $t('loyalty.status.active') }}
                  </span>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div class="bg-white rounded-lg p-4 border border-gray-200">
                    <p class="text-xs text-gray-500 mb-1">{{ $t('loyalty.label.min_points_required') }}</p>
                    <p class="text-lg font-bold text-gray-900">{{ tier.min_points_required.toLocaleString() }}</p>
                  </div>
                  <div class="bg-white rounded-lg p-4 border border-gray-200">
                    <p class="text-xs text-gray-500 mb-1">{{ $t('loyalty.label.points_multiplier') }}</p>
                    <p class="text-lg font-bold text-blue-600">{{ parseFloat(tier.points_multiplier).toFixed(2) }}x</p>
                  </div>
                  <div class="bg-white rounded-lg p-4 border border-gray-200">
                    <p class="text-xs text-gray-500 mb-1">{{ $t('loyalty.label.redemption_multiplier') }}</p>
                    <p class="text-lg font-bold text-green-600">{{ parseFloat(tier.redemption_multiplier).toFixed(2) }}x</p>
                  </div>
                </div>
                <div v-if="tier.benefits && Array.isArray(tier.benefits) && tier.benefits.length > 0" class="mt-4">
                  <p class="text-xs text-gray-500 mb-2">{{ $t('loyalty.label.benefits') }}:</p>
                  <div class="flex flex-wrap gap-2">
                    <span
                      v-for="(benefit, idx) in tier.benefits"
                      :key="idx"
                      class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      <i class="mdi mdi-check-circle mr-1"></i>
                      {{ benefit }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="ml-4 flex flex-col space-y-2">
                <button
                  @click="editTier(tier)"
                  class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors">
                  <i class="mdi mdi-pencil mr-1.5"></i>
                  {{ $t('button.edit') }}
                </button>
                <button
                  @click="deleteTier(tier.id)"
                  class="inline-flex items-center px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-lg text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors">
                  <i class="mdi mdi-delete mr-1.5"></i>
                  {{ $t('button.delete') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Tier Dialog -->
    <tier-form-dialog
      :visible="showCreateDialog || showEditDialog"
      :tier="selectedTier"
      :program-id="programId"
      @saved="handleTierSaved"
      @cancelled="closeDialog">
    </tier-form-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ErrorHandlingMixin from '@/mixins/ErrorHandlingMixin.js'
import TierFormDialog from './TierFormDialog.vue'

export default {
  name: 'TierManager',
  components: {
    TierFormDialog
  },
  mixins: [ErrorHandlingMixin],
  props: {
    programId: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      tiers: [],
      loading: false,
      showCreateDialog: false,
      showEditDialog: false,
      selectedTier: null
    }
  },
  computed: {
    sortedTiers () {
      return [...this.tiers].sort((a, b) => a.min_points_required - b.min_points_required)
    }
  },
  async mounted () {
    await this.fetchTiers()
  },
  methods: {
    async fetchTiers () {
      try {
        this.loading = true
        this.tiers = await this.$store.dispatch('loyalty/tiers/fetchTiers', this.programId)
      } catch (error) {
        console.error('Error fetching tiers:', error)
      } finally {
        this.loading = false
      }
    },
    editTier (tier) {
      this.selectedTier = tier
      this.showEditDialog = true
    },
    async deleteTier (tierId) {
      this.askQuestion(this.$t('loyalty.confirm.delete_tier'), async () => {
        try {
          await this.withLoading(async () => {
            await this.$store.dispatch('loyalty/tiers/deleteTier', {
              id: tierId,
              programId: this.programId
            })
            await this.fetchTiers()
            this.showSuccess(this.$t('loyalty.message.tier_deleted'))
            this.$emit('tier-deleted', tierId)
          }, 'loading')
        } catch (error) {
          console.error('Error deleting tier:', error)
        }
      })
    },
    handleTierSaved () {
      this.closeDialog()
      this.fetchTiers()
      this.$emit('tier-created')
    },
    closeDialog () {
      this.showCreateDialog = false
      this.showEditDialog = false
      this.selectedTier = null
    }
  }
}
</script>

<style scoped>
</style>
